import React from "react";
import MaterialTable from "material-table";
import { scaleSequential } from "d3-scale";
import { interpolateReds } from "d3-scale-chromatic";
import { Box, Button, Paper, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useLateMeterReadings } from "../hooks/useLateMeterReadings";
import { generateCSV, getTextColor } from "../utils";

const LateMeterReadingsTable = () => {
  const { data, isLoading, error } = useLateMeterReadings();

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error loading data</Typography>;
  }

  // Determine min and max days_late for color ramp
  const daysLateValues = data.map((item) => item.days_late);
  const minDaysLate = Math.min(...daysLateValues);
  const maxDaysLate = Math.max(...daysLateValues);

  const colorScale = scaleSequential(interpolateReds).domain([
    minDaysLate,
    maxDaysLate,
  ]);

  const columns = [
    {
      title: "Permit",
      field: "grouping_name",
      cellStyle: {
        width: 300,
        minWidth: 300,
      },
    },
    { title: "Meter ID", field: "meter_id" },
    {
      title: "Days Late",
      field: "days_late",
      render: ({ days_late }) => {
        const backgroundColor = colorScale(days_late);
        const textColor = getTextColor(backgroundColor);

        return (
          <div
            style={{
              backgroundColor,
              borderRadius: "4px",
              padding: "4px",
              textAlign: "center",
              color: textColor,
              fontWeight: "bold",
              width: "100%",
            }}
          >
            {days_late}
          </div>
        );
      },
    },
    {
      title: "Last Report",
      field: "c_year_c_month",
      render: (rowData) =>
        `${rowData.c_year}-${String(rowData.c_month).padStart(2, "0")}`,
    },
  ];

  const handleExportCSV = () => {
    generateCSV(
      [
        "Permit",
        "Meter ID",
        "Days Late",
        "Last Report",
        "Last Meter Reading",
        "Adjustment",
        "Adjustment Reason",
        "Active",
        "Contact Name",
        "Contact Source",
        "Contact Email",
        "Contact Phone",
      ],
      data.map((item) => [
        item.grouping_name,
        item.meter_id,
        item.days_late,
        `${item.c_year}-${String(item.c_month).padStart(2, "0")}`,
        item.meter_reading ?? "",
        item.adjustment ?? "",
        item.mr_adjustment_reason_desc || "",
        item.is_active ? "Yes" : "No",
        item.meter_contact_name || "",
        item.contact_source || "",
        item.meter_contact_email || "",
        item.meter_contact_phone || "",
      ]),
      "late-meter-readings.csv"
    );
  };

  return (
    <Paper style={{ padding: "16px" }} variant="outlined">
      <MaterialTable
        title="Late Meter Readings"
        columns={columns}
        data={data}
        options={{
          showTitle: false,
          search: false,
          paging: true,
          pageSize: 25,
          pageSizeOptions: [25, 50, 100],
          emptyRowsWhenPaging: false,
          padding: "dense",
          headerStyle: {
            backgroundColor: "#f5f5f5",
            fontWeight: "bold",
          },
        }}
        components={{
          Toolbar: () => (
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                padding="10px 0 0 0"
                bgcolor="#fff"
              >
                <Typography
                  variant="h6"
                  style={{
                    fontFamily: "Arial",
                    fontWeight: 600,
                    fontSize: "18px",
                    color: "#666",
                  }}
                >
                  Late Meter Readings
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={handleExportCSV}
                >
                  Export CSV with Contacts
                </Button>
              </Box>
              <Alert severity="info" style={{ margin: "8px 0" }}>
                Readings are late if not submitted by the 5th of the following
                month (Jan readings due Feb 5th). Export CSV for contact info
                (resolved from meter contact → permit contact → alternate
                contact → permittee record).
              </Alert>
            </Box>
          ),
        }}
        localization={{
          body: {
            emptyDataSourceMessage: "No Late Meter Readings!",
          },
        }}
      />
    </Paper>
  );
};

export default LateMeterReadingsTable;
